import { useState, useEffect, useCallback } from 'react';
import {
  auth,
  googleProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut,
  hasFirebaseConfig,
} from '../services/firebase';
import { api } from '../services/api';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsInviteCode, setNeedsInviteCode] = useState(false);

  useEffect(() => {
    // Test-login-only mode (paired with services/firebase.js stub + backend
    // /api/auth/dev-login): when Firebase is intentionally not configured on
    // this build (no VITE_FIREBASE_API_KEY) the auth state listener never
    // fires with a real user. Call the backend dev-login endpoint, which is
    // itself gated on FIREBASE_ENABLED=false on the server.
    if (!hasFirebaseConfig) {
      let cancelled = false;
      (async () => {
        try {
          const resp = await fetch(`${api.baseUrl}/api/auth/dev-login`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'package-name': api.packageName,
            },
            body: JSON.stringify({ device_id: api.getDeviceId() }),
          });
          if (cancelled) return;
          if (resp.ok) {
            const data = await resp.json();
            api.setViUserId(data.vi_user_id);
            setUser({ ...data, firebaseUser: null });
            setError(null);
          } else {
            const detail = await resp.text();
            setError(`dev-login failed: ${resp.status} ${detail}`);
          }
        } catch (e) {
          if (!cancelled) setError(`dev-login error: ${e.message}`);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && firebaseUser.isAnonymous) {
        api.setFirebaseIdToken(null);
        await signOut(auth);
        setUser(null);
        setLoading(false);
        return;
      }
      if (firebaseUser) {
        api.setFirebaseIdToken(null);
        try {
          const idToken = await firebaseUser.getIdToken();
          const pendingInviteCode = localStorage.getItem('pending_invite_code') || '';
          const headers = {
            'Content-Type': 'application/json',
            'id-token': idToken,
            'package-name': api.packageName,
          };
          if (pendingInviteCode) {
            headers['invite-code'] = pendingInviteCode;
          }
          const response = await fetch(`${api.baseUrl}/api/auth/firebase`, {
            method: 'POST',
            headers,
          });

          if (response.ok) {
            const data = await response.json();
            api.setFirebaseIdToken(idToken, firebaseUser.uid);
            setUser({
              ...data,
              firebaseUser,
            });
            api.setViUserId(data.vi_user_id);
            setNeedsInviteCode(false);
            setError(null);

            // Clear pending invite code after successful registration
            if (data.is_new_user) {
              localStorage.removeItem('pending_invite_code');
            }

            reportWebDevice().catch(() => {});
          } else if (response.status === 403 || response.status === 400) {
            // 403: new user, no invite code provided
            // 400: invalid invite code (expired, used_up, not_found, etc.)
            const data = await response.json().catch(() => ({}));
            await signOut(auth);
            setNeedsInviteCode(true);
            setError(response.status === 403
              ? 'Please enter your invite code to sign up'
              : (data.detail || 'Invalid invite code'));
          } else {
            console.error('API auth sync failed:', response.status);
            setError('Failed to sync with server');
          }
        } catch (e) {
          console.error('Failed to sync with API server:', e);
          setError(e.message);
        }
      } else {
        api.setFirebaseIdToken(null);
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Sign out failed:', e);
    }
    setUser(null);
    api.setViUserId(null);
    api.setFirebaseIdToken(null);
  }, []);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    needsInviteCode,
    loginWithGoogle,
    logout,
  };
}

async function reportWebDevice() {
  const deviceId = api.getDeviceId();
  await api.reportDevice({
    device_id: deviceId,
    device_token: null,
    store: 'web',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    timezone: new Date().getTimezoneOffset() * -1,
    user_agent: navigator.userAgent,
  });
}
